import React, { useState, useEffect } from "react";
import {
  Stack,
  Checkbox,
  TextInput,
  Text,
  Select,
  MultiSelect,
  SimpleGrid,
  Divider,
  Paper,
} from "@mantine/core";
import RemoteSelect2 from "../remoteSelect2";
import McaNotificationTemplateInput from "./McaNotificationTemplateInput";
import {
  defaultStepNotification,
  formatStaffLabel,
  MCA_NOTIFY_BY,
  MCA_NOTIFY_CHANNELS,
} from "../../data/mcaEntities";

const SectionTitle = ({ children }) => (
  <Text size="sm" weight={600}>
    {children}
  </Text>
);

const McaStepNotificationForm = ({ value, onChange }) => {
  const notification = { ...defaultStepNotification(), ...value };
  const [emailOptions, setEmailOptions] = useState(() => notification.notifyEmails ?? []);

  useEffect(() => {
    setEmailOptions((prev) => {
      const merged = [...prev];
      (notification.notifyEmails ?? []).forEach((e) => {
        if (e && !merged.includes(e)) merged.push(e);
      });
      return merged;
    });
  }, [notification.notifyEmails]);

  const patch = (p) => onChange({ ...notification, ...p });

  const handleNotifyByChange = (notifyBy) => {
    patch({
      notifyBy,
      notifyEmails: notifyBy === "EMAILS" ? notification.notifyEmails : [],
      notifyUsers: notifyBy === "USERS" ? notification.notifyUsers : [],
      notifyRoles: notifyBy === "USER_ROLE" ? notification.notifyRoles : [],
    });
  };

  const isEmail = (notification.channel ?? "EMAIL") === "EMAIL";

  return (
    <Stack spacing="lg">
      <Stack spacing="md">
        <SectionTitle>Basic</SectionTitle>
        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
          <TextInput
            label="Name"
            required
            size="sm"
            placeholder="Notify maker on complete"
            value={notification.name ?? ""}
            onChange={(e) => patch({ name: e.target.value })}
          />
          <Select
            label="Channel"
            size="sm"
            data={MCA_NOTIFY_CHANNELS}
            value={notification.channel ?? "EMAIL"}
            onChange={(v) => patch({ channel: v ?? "EMAIL" })}
          />
        </SimpleGrid>
      </Stack>

      <Divider />

      <Stack spacing="md">
        <SectionTitle>When to send</SectionTitle>
        <Stack spacing="xs">
          <Checkbox
            label="Send on start of the step"
            checked={!!notification.notifyOnStart}
            onChange={(e) => patch({ notifyOnStart: e.currentTarget.checked })}
          />
          <Checkbox
            label="Send on completion of the step"
            checked={notification.notifyOnComplete !== false}
            onChange={(e) => patch({ notifyOnComplete: e.currentTarget.checked })}
          />
        </Stack>
      </Stack>

      <Divider />

      <Stack spacing="md">
        <SectionTitle>Recipients</SectionTitle>
        <Select
          label="Notify to"
          size="sm"
          data={MCA_NOTIFY_BY}
          value={notification.notifyBy ?? "USERS"}
          onChange={handleNotifyByChange}
        />
        {notification.notifyBy === "EMAILS" && (
          <MultiSelect
            label="Email addresses"
            size="sm"
            placeholder="Type email and press Enter"
            data={emailOptions}
            searchable
            creatable
            value={notification.notifyEmails ?? []}
            getCreateLabel={(query) => `+ Add ${query}`}
            onCreate={(query) => {
              const trimmed = query.trim();
              if (!trimmed) return null;
              setEmailOptions((current) =>
                current.includes(trimmed) ? current : [...current, trimmed]
              );
              return trimmed;
            }}
            onChange={(notifyEmails) => patch({ notifyEmails })}
          />
        )}
        {notification.notifyBy === "USERS" && (
          <RemoteSelect2
            form={{
              values: notification,
              setFieldValue: (field, val) => patch({ [field]: val }),
              markUserEdited: () => {},
            }}
            name="notifyUsers"
            label="Notify users"
            apiEntity="user"
            labelRender={formatStaffLabel}
            multiple
            size="sm"
            placeholder="Search users"
            searchFields={["name", "staffId", "cname", "email"]}
          />
        )}
        {notification.notifyBy === "USER_ROLE" && (
          <RemoteSelect2
            form={{
              values: notification,
              setFieldValue: (field, val) => patch({ [field]: val }),
              markUserEdited: () => {},
            }}
            name="notifyRoles"
            label="Notify roles"
            apiEntity="userRole"
            labelField="name"
            multiple
            size="sm"
            placeholder="Search roles"
            searchFields={["name"]}
          />
        )}
        <Paper withBorder p="sm" radius="sm">
          <Text size="xs" weight={600} mb="xs" color="dimmed">
            Additional recipients
          </Text>
          <Stack spacing="xs">
            <Checkbox
              label="Maker"
              checked={!!notification.notifyMaker}
              onChange={(e) => patch({ notifyMaker: e.currentTarget.checked })}
            />
            <Checkbox
              label="Next step approvers"
              checked={!!notification.notifyNextApprovers}
              onChange={(e) =>
                patch({ notifyNextApprovers: e.currentTarget.checked })
              }
            />
          </Stack>
        </Paper>
      </Stack>

      {isEmail && (
        <>
          <Divider />
          <Stack spacing="md">
            <SectionTitle>Email content</SectionTitle>
            <McaNotificationTemplateInput
              label="Email subject"
              required
              placeholder="Step completed: {{requestCode}}"
              value={notification.subject ?? ""}
              onChange={(subject) => patch({ subject })}
            />
            <McaNotificationTemplateInput
              label="Email message"
              required
              multiline
              placeholder="Request {{requestCode}} — step {{stepName}}"
              value={notification.message ?? ""}
              onChange={(message) => patch({ message })}
            />
          </Stack>
        </>
      )}

      {!isEmail && (
        <Text size="sm" color="dimmed">
          SMS delivery is not configured yet. This rule will be saved but not sent.
        </Text>
      )}
    </Stack>
  );
};

export default McaStepNotificationForm;
